import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { actions as assetActions } from '../../actions/asset';
import { actions as contentActions } from '../../actions/content';
import { actions as appActions } from '../../actions/app';
import { actions as axisDeficientAssetUrlActions } from '../../actions/axisDeficientAssetUrl';
import { actions as axisOrderActions } from '../../actions/axisOrder';
import AssetEditor from '../../components/Editor/';
import DraggablePopup from '../../components/DraggablePopup';
import ConfirmCloseDialog from '../../components/OrderForm/ConfirmCloseDialog';
import ContentSelectionLayout from '../../layouts/ContentSelectionLayout';
import OrderGraphicLayout from '../../layouts/OrderGraphicLayout';
import BuildGraphicLayout from '../../layouts/BuildGraphicLayout';
import LuciPropTypes from '../../propTypes/';
import StringUtils from '../../util/stringUtils';
import { Container, StickyFooter, StickyHeader } from '../../styled-components/Layout';
import { makeGetReplaceable } from '../../selectors/asset';
import ClipperFooter from './ClipperFooter';
import ClipperHeader from './ClipperHeader';

const Header = styled(StickyHeader)`
  background-color: ${props => (props.editMode ? '#5379a2' : '#636262')};
  color: white;
  padding-left: 0.5rem;
`;

const Footer = styled(StickyFooter)`
  background-color: #d7d6d6;
`;

class ClipperTemplateEditor extends PureComponent {
  constructor() {
    super();

    this.state = {
      contentSelectionDialogVisible: false,
      orderGraphicDialogVisible: false,
      buildGraphicDialogVisible: false,
      confirmCancelOrderDialogVisible: false
    };
  }
  UNSAFE_componentWillMount() {
    if (this.props.channelList.length === 0) {
      this.props.loadChannelList();
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      nextProps.axisDeficientAssetUrl &&
      nextProps.axisDeficientAssetUrl !== this.props.axisDeficientAssetUrl
    ) {
      window.open(
        nextProps.axisDeficientAssetUrl,
        '_blank',
        'fullscreen=1,location=0,titlebar=0,menubar=0'
      );
      axisDeficientAssetUrlActions.clearAxisDeficientAssetUrl();
    }
  }

  getOrderDialogTitle = () =>
    this.props.assetType.toLowerCase() === 'image' ? 'Order Image' : 'Order Movie';

  openAssetBrowserDialog = (assetType, assetCategory, restrictAssetCategory) => {
    // only show if order dialog is not shown
    if (!this.state.contentSelectionDialogVisible) {
      if (assetType) {
        this.props.loadContent(assetType, assetCategory, restrictAssetCategory);
      }
      this.setState({
        contentSelectionDialogVisible: true
      });
    }
  };

  closeAssetBrowserDialog = () => {
    this.setState({
      contentSelectionDialogVisible: false
    });
  };

  openOrderGraphicDialog = () => {
    this.props.startOrder(this.props.selectedReplaceable);
    this.setState({
      contentSelectionDialogVisible: false,
      orderGraphicDialogVisible: true
    });
  };

  closeOrderGraphicDialog = () => {
    if (this.props.orderHasChanges) {
      this.setState({ confirmCancelOrderDialogVisible: true });
    } else {
      this.setState({
        orderGraphicDialogVisible: false
      });
    }
  };

  handleClickBuildGraphic = () => {
    const { properties } = this.props.selectedReplaceable;

    const isAxisReplaceable =
      properties.axisTemplate || properties.axisStyle || properties['axis template id'];

    return isAxisReplaceable ? this.openAxisWindow() : this.openBuildGraphicDialog();
  };

  openBuildGraphicDialog = () => {
    this.setState({
      buildGraphicDialogVisible: true
    });
  };

  openAxisWindow = () => {
    // create deficient asset, on success, open new window with url from response
    this.props.loadAxisDeficientAssetUrl(this.props.selectedReplaceable);
  };

  closeBuildGraphicDialog = () => {
    this.setState({
      buildGraphicDialogVisible: false
    });
  };

  render() {
    const { assetType, isMosObject, title } = this.props;
    const {
      contentSelectionDialogVisible,
      orderGraphicDialogVisible,
      buildGraphicDialogVisible
    } = this.state;
    return (
      <Container>
        <DraggablePopup
          title={StringUtils.capitalize(assetType)}
          closeDialog={this.closeAssetBrowserDialog}
          axis="both"
          z-index={100}
          handle=".handle"
          visible={contentSelectionDialogVisible}
          resizable
        >
          <ContentSelectionLayout
            hideSideBar
            openOrderGraphicDialog={this.openOrderGraphicDialog}
            openBuildGraphicDialog={this.handleClickBuildGraphic}
            onConfirmAssetSelected={this.closeAssetBrowserDialog}
          />
        </DraggablePopup>
        <DraggablePopup
          title={this.getOrderDialogTitle()}
          closeDialog={this.closeOrderGraphicDialog}
          axis="both"
          z-index={100}
          handle=".handle"
          height={430}
          visible={orderGraphicDialogVisible}
        >
          <OrderGraphicLayout />
        </DraggablePopup>
        <DraggablePopup
          title="Compose Image"
          closeDialog={this.closeBuildGraphicDialog}
          axis="both"
          z-index={100}
          handle=".handle"
          minHeight={400}
          width={'auto'}
          visible={buildGraphicDialogVisible}
        >
          <BuildGraphicLayout onAssembleComplete={this.closeBuildGraphicDialog} visible={buildGraphicDialogVisible} />
        </DraggablePopup>
        <ConfirmCloseDialog
          isOpen={this.state.confirmCancelOrderDialogVisible}
          onCancelClick={() => this.setState({ confirmCancelOrderDialogVisible: false })}
          onOkClick={() =>
            this.setState({
              orderGraphicDialogVisible: false,
              confirmCancelOrderDialogVisible: false
            })
          }
        />
        <Header editMode={isMosObject}>
          {}
          <ClipperHeader
            editMode={isMosObject}
            title={`${isMosObject ? 'EDIT MODE - ' : ''}${title}`}
            close={this.props.exitTemplateEdit}
          />
        </Header>
        <AssetEditor openDialog={this.openAssetBrowserDialog} verticalLayout />
        <Footer>
          <ClipperFooter />
        </Footer>
      </Container>
    );
  }
}

ClipperTemplateEditor.defaultProps = {
  channelList: [],
  axisDeficientAssetUrl: '',
  title: ''
};

ClipperTemplateEditor.propTypes = {
  channelList: LuciPropTypes.ChannelList,
  loadChannelList: PropTypes.func.isRequired,
  loadContent: PropTypes.func.isRequired,
  loadAxisDeficientAssetUrl: PropTypes.func.isRequired,
  startOrder: PropTypes.func.isRequired,
  assetType: PropTypes.string.isRequired,
  axisDeficientAssetUrl: PropTypes.string,
  selectedReplaceable: LuciPropTypes.Replaceable.isRequired,
  orderHasChanges: PropTypes.bool.isRequired,
  isMosObject: PropTypes.bool.isRequired,
  title: PropTypes.string,
  exitTemplateEdit: PropTypes.func.isRequired
};

const makeMapStateToProps = () => {
  const getReplaceable = makeGetReplaceable();

  const mapStateToProps = state => ({
    channelList: state.asset.channelList,
    sidebar: state.app.sidebar,
    assetType: state.content.assetType,
    axisDeficientAssetUrl: state.axisDeficientAssetUrl.url,
    selectedReplaceable: getReplaceable(state, state.asset.selectedReplaceableId),
    orderHasChanges: state.axisOrder.hasChanges,
    isMosObject: state.asset.isMosObject,
    title: state.asset.isMosObject ? state.asset.asset.abstract : state.asset.asset.title
  });
  return mapStateToProps;
};

const mapActionCreators = {
  loadChannelList: assetActions.loadChannelList,
  loadContent: contentActions.loadContent,
  changeSidebarState: appActions.changeSidebarState,
  loadAxisDeficientAssetUrl: axisDeficientAssetUrlActions.createAxisDeficientAsset,
  startOrder: axisOrderActions.startOrder,
  exitTemplateEdit: assetActions.endEditAsset
};

export default connect(
  makeMapStateToProps,
  mapActionCreators
)(ClipperTemplateEditor);



// WEBPACK FOOTER //
// ./app/components/Clipper/ClipperTemplateEditor.js